import axios, { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { DefaultGetAllResponse } from '~/App/Api/rest';

import usePagination, { PaginationOptions } from '~/react-hooks/usePagination';
import { UserWebAsset } from '~/types/models';
import { SocialType } from '~/App/Modules/Socials/util';

export interface UserWebAssetsFilter {
  page?: number;
  per_page?: number;
}

export const userWebAssetsQueryKeys = {
  all: ['user-web-assets'],
  lists: () => [...userWebAssetsQueryKeys.all, 'list'] as const,
  list: (filters: UserWebAssetsFilter) =>
    [...userWebAssetsQueryKeys.lists(), { ...filters }] as const,
};

/**
 * User Web Asset API actions
 */
const apiUrl = '/api/v3/user-web-assets';

export const getUserWebAssets = async (params: UserWebAssetsFilter) => {
  const response = await axios.get<DefaultGetAllResponse<UserWebAsset>>(
    apiUrl,
    {
      params,
    }
  );

  return response.data;
};

export interface CreateUserWebAssetParams {
  url: string;
  type: SocialType;
}

export interface CreateUserWebAssetResponse {
  message: string;
  data: UserWebAsset;
}

export const createUserWebAsset = async ({
  url,
  type,
}: CreateUserWebAssetParams) => {
  const response = await axios.post<CreateUserWebAssetResponse>(apiUrl, {
    url,
    type,
  });

  return response.data;
};

export type UpdateUserWebAssetParams = CreateUserWebAssetParams & {
  uuid: string;
};

export const updateUserWebAsset = async ({
  uuid,
  ...params
}: UpdateUserWebAssetParams) => {
  const response = await axios.put<UserWebAsset>(`${apiUrl}/${uuid}`, params);

  return response.data;
};

export interface DeleteUserWebAssetParams {
  uuid: string;
}

export interface DeleteUserWebAssetResponse {
  message: string;
}

export const deleteUserWebAsset = async ({
  uuid,
}: DeleteUserWebAssetParams) => {
  const response = await axios.delete<DeleteUserWebAssetResponse>(
    `${apiUrl}/${uuid}`
  );

  return response.data;
};

/**
 * User Web Asset Hooks
 */
export interface UseUserWebAssetsParams {
  filters: UserWebAssetsFilter;
  options?: UseQueryOptions<DefaultGetAllResponse<UserWebAsset>, AxiosError>;
}

export function useCreateUserWebAsset(
  options?: UseMutationOptions<
    CreateUserWebAssetResponse,
    AxiosError,
    CreateUserWebAssetParams
  >
) {
  const queryClient = useQueryClient();

  return useMutation(createUserWebAsset, {
    onSuccess: () => {
      void queryClient.invalidateQueries(userWebAssetsQueryKeys.lists());
    },
    ...options,
  });
}

export function useDeleteUserWebAsset(
  options?: UseMutationOptions<
    DeleteUserWebAssetResponse,
    AxiosError,
    DeleteUserWebAssetParams
  >
) {
  const queryClient = useQueryClient();

  return useMutation(deleteUserWebAsset, {
    onSuccess: () => {
      void queryClient.invalidateQueries(userWebAssetsQueryKeys.lists());
    },
    ...options,
  });
}

export function useUserWebAssetsPagination(params: PaginationOptions) {
  return usePagination<UserWebAsset>(apiUrl, params);
}
